// frontend/src/features/content/ContentList.js
import { useGetAllContentAdminQuery } from "./contentApiSlice";
import Content from "./Content";
import useAuth from "../../hooks/useAuth";
import useTitle from "../../hooks/useTitle";
import { Spinner, Alert, Pagination, Form, Row, Col, Button } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import {
    setPageNumber,
    setSearchQuery,
    setIsActiveFilter,
    setCategoryFilter,
    setClanguageFilter,
    resetContentListState
} from './contentSlice';
import { useGetAllCategoriesAdminQuery, selectAllCategoriesForAdminByArgs } from '../categories/categoriesApiSlice';

const ContentList = () => {
    useTitle('Nirakar Satguru: Contents List');

    const { isManager, isAdmin } = useAuth();

    const dispatch = useDispatch();
    const { pageNumber, searchQuery, isActiveFilter, categoryFilter, ClanguageFilter } = useSelector((state) => state.content);

    const [localIsActiveFilter, setLocalIsActiveFilter] = useState(isActiveFilter || '');
    const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery || '');
    const [localCategoryFilter, setLocalCategoryFilter] = useState(categoryFilter || '');
    const [localClanguageFilter, setLocalClanguageFilter] = useState(ClanguageFilter || '');

    const queryArgs = useMemo(() => {
        const args = {
            page: pageNumber,
            search: searchQuery,
            isActive: isActiveFilter,
            category: categoryFilter,
            Clanguage: ClanguageFilter // ✅ सुनिश्चित करें कि यह यहाँ है
        };
        console.log('ContentList - Query Arguments being sent:', args);
        return args;
    }, [pageNumber, searchQuery, isActiveFilter, categoryFilter, ClanguageFilter]);


    const {
        data: contentData,
        isLoading,
        isSuccess,
        isError,
        error
    } = useGetAllContentAdminQuery(queryArgs);

    const { data: categoriesResult, isLoading: isCategoriesLoading } = useGetAllCategoriesAdminQuery({ page: 1, search: '', isActive: 'true' });
    const categories = useSelector(state => selectAllCategoriesForAdminByArgs(state, { page: 1, search: '', isActive: 'true' }));


    useEffect(() => {
        if (isSuccess) {
            console.log('ContentList - Data received from API (contentData):', contentData);
        }
        if (isError) {
            console.error('ContentList - Error fetching data:', error);
        }
        // Sync local states with Redux store on component mount or Redux state change
        setLocalIsActiveFilter(isActiveFilter || '');
        setLocalSearchQuery(searchQuery || '');
        setLocalCategoryFilter(categoryFilter || '');
        setLocalClanguageFilter(ClanguageFilter || '');
    }, [isSuccess, isError, contentData, error, isActiveFilter, searchQuery, categoryFilter, ClanguageFilter]);


    const handlePageChange = (page) => {
        dispatch(setPageNumber(page));
    };

    const handleApplyFilters = () => {
        dispatch(setSearchQuery(localSearchQuery));
        dispatch(setIsActiveFilter(localIsActiveFilter));
        dispatch(setCategoryFilter(localCategoryFilter));
        dispatch(setClanguageFilter(localClanguageFilter)); // ✅ सुनिश्चित करें कि यह डिस्पैच हो रहा है
        dispatch(setPageNumber(1)); // Reset to first page on applying filters
    };

    const handleClearFilters = () => {
        setLocalSearchQuery('');
        setLocalIsActiveFilter('');
        setLocalCategoryFilter('');
        setLocalClanguageFilter('');
        dispatch(resetContentListState()); // Reset Redux state
    };


    let content;

    if (isLoading || isCategoriesLoading) {
        content = <div className="text-center my-5"><Spinner animation="border" role="status" /><p className="mt-2">Loading contents...</p></div>;
    } else if (isError) {
        content = <Alert variant="danger" className="text-center my-5">{error?.data?.message || 'Error loading contents.'}</Alert>;
    } else if (isSuccess) {
        const { ids: filteredContentIds, totalPages, currentPage, filteredContentsCount } = contentData;

        const contentTableContent = filteredContentIds?.length
            ? filteredContentIds.map((contentId, index) => <Content key={contentId} contentId={contentId} index={index} />)
            : null;

        content = (
            <>
                <Row className="mb-3 align-items-center">
                    <Col md={3}>
                        <Form.Group controlId="searchQuery">
                            <Form.Control
                                type="text"
                                placeholder="Search by title..."
                                value={localSearchQuery}
                                onChange={(e) => setLocalSearchQuery(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="isActiveFilter">
                            <Form.Select
                                value={localIsActiveFilter}
                                onChange={(e) => setLocalIsActiveFilter(e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="categoryFilter">
                            <Form.Select
                                value={localCategoryFilter}
                                onChange={(e) => setLocalCategoryFilter(e.target.value)}
                            >
                                <option value="">All Categories</option>
                                {categories?.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="ClanguageFilter">
                            <Form.Select
                                value={localClanguageFilter}
                                onChange={(e) => setLocalClanguageFilter(e.target.value)}
                            >
                                <option value="">All Languages</option>
                                <option value="hi">Hindi</option>
                                <option value="en">English</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={12} className="d-flex justify-content-end mt-2">
                        <Button variant="primary" onClick={handleApplyFilters} className="me-2">
                            Apply Filters
                        </Button>
                        <Button variant="secondary" onClick={handleClearFilters}>
                            Clear Filters
                        </Button>
                    </Col>
                </Row>

                {(isManager || isAdmin) && (
                    <Link to="/dashboard/contents/new" className="btn btn-success mb-3">
                        Add New Content
                    </Link>
                )}

                <div className="table-responsive">
                    <table className="table table-striped table-bordered table-hover">
                        <thead className="thead-dark">
                            <tr>
                                <th scope="col">#</th>
                                <th scope="col">Title</th>
                                
                                <th scope="col">Content Body</th>
                                <th scope="col">Category</th>
                                <th scope="col">Image</th>
                                <th scope="col">Clanguage</th>
                                <th scope="col">Active</th>
                                <th scope="col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {contentTableContent}
                        </tbody>
                    </table>
                </div>

                {!isLoading && !isError && filteredContentIds.length === 0 && (
                    <p className="text-center mt-4">No contents found matching your criteria.</p>
                )}

                {totalPages > 1 && (
                    <Pagination className="justify-content-center mt-4">
                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                        {[...Array(totalPages)].map((_, i) => (
                            <Pagination.Item
                                key={i + 1}
                                active={i + 1 === currentPage}
                                onClick={() => handlePageChange(i + 1)}
                            >
                                {i + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                    </Pagination>
                )}
            </>
        );
    }

    return content;
};

export default ContentList;